import {Action, Card, DataStatus, openDeleteDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useRemoveMinipurchaseOfferedVehicleMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  getVehicleSaleTypeFlagProps,
  handleApiError,
  useInterestEditPermissions,
  usePricePermissions,
  VehicleWidgetCard,
} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useInterestData} from '../hooks/useInterestData';

interface OfferedVehiclesProps extends RequiredTestIdProps {
  interestId: string;
}

export function OfferedVehicles(props: OfferedVehiclesProps) {
  const formatPrice = useFormatCurrency();

  const {canViewAnyOfVehiclePriceFields: canReadPrices} = usePricePermissions({
    vehicleRecordId: null,
    businessCaseRecordId: null,
  });

  const {hasRemoveMinipurchaseOfferedVehicleInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const {interest, offeredVehiclesList, miniPurchase, isLoading, isError} = useInterestData(
    props.interestId
  );

  const [removeMinipurchaseOfferedVehicle, {isLoading: isRemoveMinipurchaseOfferedVehicleLoading}] =
    useRemoveMinipurchaseOfferedVehicleMutation();

  const onRemoveVehicle = (vehicleId: string) => {
    if (!miniPurchase) {
      showNotification.error('missing miniPurchase object');
      return;
    }

    removeMinipurchaseOfferedVehicle({
      minipurchaseId: miniPurchase.id,
      vehicleId,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus minHeight={25} isLoading={isLoading} isError={isError}>
      <Show when={offeredVehiclesList}>
        {(offeredVehiclesList) => (
          <Card title={i18n.t('entity.interest.labels.sellingTab')}>
            <VehicleWidgetCard
              data-testid={suffixTestId('offeredList', props)}
              currentInterestState={interest?.state}
              vehicles={offeredVehiclesList.map((vehicle, index) => ({
                id: vehicle.id,
                vin: vehicle.vin,
                licencePlate: vehicle.vehicle.registrationPlate,
                firstRegistration: vehicle.vehicle.firstRegistrationOn,
                make: vehicle.make,
                vehicleType: vehicle.type?.toString(),
                modelFamily: vehicle.modelFamily,
                trim: vehicle.trim,
                title: vehicle.title,
                variant: vehicle.variant,
                mileage: vehicle.vehicle.mileage,
                transmission: vehicle.transmission,
                fuelType: vehicle.fuelType,
                bodyStyle: vehicle.bodyStyle,
                driveType: vehicle.drive,
                photos: vehicle.photos,
                manufacturedOn: vehicle.vehicle.manufacturedOn,
                flags: [getVehicleSaleTypeFlagProps(vehicle.saleType)].filter(isNotNil),

                firstParameter:
                  // TODO: this ACL condition is not tied to recordId, should we abstract it?
                  canReadPrices && vehicle.sellingPrice.withoutVat
                    ? formatPrice(
                        Number(vehicle.sellingPrice.withoutVat.amount),
                        vehicle.sellingPrice.withoutVat.currency,
                        0
                      )
                    : null,
                secondParameter:
                  canReadPrices && vehicle.sellingPrice.withVat
                    ? formatPrice(
                        Number(vehicle.sellingPrice.withVat.amount),
                        vehicle.sellingPrice.withVat.currency,
                        0
                      )
                    : null,
                actions: buildArray<Action>()
                  .add({
                    'data-testid': suffixTestId(`offeredList-delete-[${index}]`, props),
                    type: 'button',
                    leftIcon: 'action/delete',
                    variant: 'outlined',
                    title: i18n.t('general.labels.delete'),
                    isDisabled:
                      isNil(miniPurchase) ||
                      isRemoveMinipurchaseOfferedVehicleLoading ||
                      interest?.state === 'UNSUCCESSFUL' ||
                      interest?.state === 'CLOSED' ||
                      !hasRemoveMinipurchaseOfferedVehicleInterestPermission,
                    onClick: () =>
                      openDeleteDialog({
                        'data-testid': suffixTestId(`offeredList-delete-[${index}]`, props),
                        onConfirm: () => onRemoveVehicle(vehicle.id),
                      }),
                  })
                  .add({
                    'data-testid': suffixTestId(`offeredList-detail-[${index}]`, props),
                    type: 'button',
                    title: i18n.t('entity.vehicle.labels.detail'),
                    variant: 'outlined',
                    leftIcon: 'action/launch',
                    onClick: () => {
                      window.open(
                        composePath(vehiclesRoutes.detail, {params: {id: vehicle.id}}),
                        '_blank'
                      );
                    },
                  }),
              }))}
            />
          </Card>
        )}
      </Show>
    </DataStatus>
  );
}
